// Pure derivations from the backend preview response → the numbers/labels the
// dashboard renders. All money figures are per-round (per one "ลงของ" cycle),
// matching the design's "/รอบ" framing.

import dayjs from "dayjs";
import {
  CustomerScoreResult,
  FormulaScoreView,
} from "../../types/formula-scorer.type";

// A win whose % is large but whose real per-round baht is tiny is flagged so a
// reviewer doesn't chase a headline percentage that moves almost no money.
const LOW_MONEY_PCT = 30; // "high" percentage
const LOW_MONEY_BAHT_PER_ROUND = 20; // but only this much money per round

export interface DerivedRow {
  result: CustomerScoreResult;
  current: FormulaScoreView | undefined;
  winner: FormulaScoreView | null;
  rounds: number;
  coverageDays: number;
  gainTotal: number; // baht over the whole window
  gainPerRound: number; // baht per round
  gainPct: number; // % less loss vs current
  wasteReductionPerRound: number; // baht/round from less over-loading
  recoveryPerRound: number; // baht/round from fewer stockouts
  isLowMoney: boolean;
}

const findScore = (r: CustomerScoreResult, code: string | null | undefined) =>
  code ? r.scores.find((s) => s.formula_code === code) : undefined;

export function deriveRow(result: CustomerScoreResult): DerivedRow {
  const current = findScore(result, result.current_formula_code);
  const winner = findScore(result, result.winner_formula_code) ?? null;
  const rounds = winner?.rounds_evaluated ?? current?.rounds_evaluated ?? 0;

  const curLoss = current?.net_loss ?? 0;
  const winLoss = winner?.net_loss ?? 0;
  const gainTotal = Math.max(0, curLoss - winLoss);
  const gainPerRound = rounds > 0 ? gainTotal / rounds : 0;
  const gainPct = winner?.improvement_pct_vs_current ?? 0;

  const wasteReduction = Math.max(0, (current?.waste_loss ?? 0) - (winner?.waste_loss ?? 0));
  const recovery = Math.max(0, (current?.stockout_loss ?? 0) - (winner?.stockout_loss ?? 0));

  const coverageDays =
    dayjs(result.window_end).diff(dayjs(result.window_start), "day") || 0;

  const isLowMoney =
    result.recommendation === "switch" &&
    gainPct >= LOW_MONEY_PCT &&
    gainPerRound > 0 &&
    gainPerRound < LOW_MONEY_BAHT_PER_ROUND;

  return {
    result,
    current,
    winner,
    rounds,
    coverageDays,
    gainTotal,
    gainPerRound,
    gainPct,
    wasteReductionPerRound: rounds > 0 ? wasteReduction / rounds : 0,
    recoveryPerRound: rounds > 0 ? recovery / rounds : 0,
    isLowMoney,
  };
}

export interface ScorerSummary {
  total: number;
  switchCount: number;
  switchBahtPerRound: number;
  pending: number;
  confirmed: number;
  rejected: number;
  keep: number;
  insufficient: number;
}

export function summarize(rows: DerivedRow[]): ScorerSummary {
  const s: ScorerSummary = {
    total: rows.length,
    switchCount: 0,
    switchBahtPerRound: 0,
    pending: 0,
    confirmed: 0,
    rejected: 0,
    keep: 0,
    insufficient: 0,
  };
  for (const row of rows) {
    const { recommendation, decision } = row.result;
    if (recommendation === "switch") {
      s.switchCount += 1;
      s.switchBahtPerRound += row.gainPerRound;
    } else if (recommendation === "keep") {
      s.keep += 1;
    } else {
      s.insufficient += 1;
    }
    if (decision === "pending") s.pending += 1;
    else if (decision === "confirmed") s.confirmed += 1;
    else if (decision === "rejected") s.rejected += 1;
  }
  return s;
}

export const fmtBaht = (n: number): string =>
  Math.round(n).toLocaleString("en-US");

export const fmtPct = (n: number): string => (Math.round(n * 10) / 10).toString();
